import { registerLicense } from '@primeui/license-manager';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';
import { createApp } from 'vue';
import 'primeicons/primeicons.css';
import '@/assets/scss/main.scss';
import router from '@/router';
import { getPrimeUiLicenseKey } from '@/utils/env';
import { adminPassThrough } from '@/utils/primevue-pt';
import { AdminToastService } from '@/utils/toast-policy';
import App from './App.vue';

/**
 * Bind PrimeVue's `primary` palette to the admin brand indigo.
 *
 * Aura ships an emerald primary (`#10b981`). Against white that is 2.53:1 —
 * a WCAG 2 AA failure (needs 4.5:1) that axe reports as `serious`, and it hit
 * every primary button (white label on emerald) and every active tab (emerald
 * label on white) in the admin.
 *
 * The brand primary has always been indigo — `$color-primary: #4f46e5` in
 * `_tokens.scss` (6.29:1 on white, AA-clean). `main.scss` once carried a
 * `.p-button-primary` override trying to force exactly this; it was dead code
 * (PrimeVue renders a default Button as `p-button p-component` and never emits
 * `p-button-primary`) and was removed in the 2026-10-08 WCAG 2.2 pass. Fixing
 * the palette at the preset is the real remedy; the ramp keeps `500` on the brand
 * token so `primary.500` (the light-scheme button background) stays AA-compliant.
 */
const AdminPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#4f46e5', // $color-primary — 6.29:1 on white
      600: '#4338ca', // $color-primary-hover
      700: '#3730a3',
      800: '#312e81',
      900: '#26235e',
      950: '#1b193f',
    },
  },
  components: {
    button: {
      // Aura colours severity TEXT and OUTLINED buttons with the -500 hue,
      // which fails WCAG 1.4.3 as label text: orange.500 2.67:1, red.500
      // 3.59:1, green.500 2.17:1 on the #f8fafc page. The nearest step of the
      // same hue that clears 4.5:1 on both #f8fafc and white is -700:
      // orange.700 #c2410c 4.95:1, red.700 #b91c1c 6.18:1, green.700 #15803d
      // 4.79:1 (on #f8fafc). Filled buttons and the dark scheme are unchanged.
      outlined: {
        success: { color: 'light-dark({green.700}, {green.400})' },
        warn: { color: 'light-dark({orange.700}, {orange.400})' },
        danger: { color: 'light-dark({red.700}, {red.400})' },
      },
      text: {
        success: { color: 'light-dark({green.700}, {green.400})' },
        warn: { color: 'light-dark({orange.700}, {orange.400})' },
        danger: { color: 'light-dark({red.700}, {red.400})' },
      },
    },
    // Error toast and Message text: red.600 #dc2626 on their red.50 tint is
    // 4.41:1. red.700 #b91c1c is 5.92:1 there (WCAG 1.4.3).
    toast: {
      error: { color: 'light-dark({red.700}, {red.500})' },
    },
    // Warn Message text: yellow.600 #ca8a04 on its yellow.50 tint is 2.84:1
    // (axe on /csam and /albums/:id). yellow.700 #a16207 is 4.76:1 there.
    message: {
      error: { color: 'light-dark({red.700}, {red.500})' },
      warn: { color: 'light-dark({yellow.700}, {yellow.500})' },
    },
    // An unselected ToggleButton (each SelectButton option) labels in
    // surface.500 #64748b on its surface.100 #f1f5f9 track: 4.34:1. One step
    // darker, surface.600 #475569, is 6.92:1 (WCAG 1.4.3). Hover stays 700.
    togglebutton: {
      root: { color: 'light-dark({surface.600}, {surface.400})' },
    },
  },
});

/**
 * Register the PrimeUI licence before any PrimeVue component mounts.
 *
 * PrimeVue 5 is commercial PrimeUI (see `node_modules/primevue/LICENSE.md`).
 * With no key registered, `@primeui/license-manager` paints a fixed red
 * "Invalid PrimeUI License" badge over the app — and it does so inside a
 * `attachShadow({ mode: 'closed' })` root, so it is invisible to
 * `document.body.innerHTML` and `querySelectorAll` and only ever shows up in
 * pixels. That is why it reached the deployed admin unnoticed and why the
 * visual-regression suite was the thing that caught it.
 *
 * ShareLocket qualifies for the free Community tier. Removing or hiding the
 * badge is expressly forbidden by the licence terms, so the key is the only
 * legitimate fix. The key is a per-project client key like the FormKit Pro
 * key, not a secret, so shipping it to the browser is correct. When unset
 * (e.g. a fresh clone) we skip registration and the badge reappears —
 * deliberately loud rather than silently degraded.
 */
const primeUiLicenseKey = getPrimeUiLicenseKey();
if (primeUiLicenseKey) {
  registerLicense({ primeui: primeUiLicenseKey });
}

const app = createApp(App);

app.use(createPinia());
app.use(router);
app.use(PrimeVue, {
  theme: {
    preset: AdminPreset,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: false,
    },
  },
  pt: adminPassThrough,
});
app.use(AdminToastService);
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);

app.mount('#app');
